/**
 * INDEXING TYPES - Tipos para la capa de indexación
 * 
 * Define tipos para:
 * - Generación de embeddings
 * - Indexación de documentos
 * - Gestión de vectores
 */

// ============================================
// EMBEDDINGS
// ============================================

export interface EmbeddingRequest {
    /** Texto a convertir en embedding */
    text: string;
    /** Modelo a usar (default: text-embedding-004) */
    model?: EmbeddingModel;
}

export interface EmbeddingResult {
    /** Vector resultante */
    embedding: number[];
    /** Modelo usado */
    model: EmbeddingModel;
    /** Dimensiones del vector */
    dimensions: number;
    /** Tokens procesados */
    tokensUsed?: number;
}

export interface BatchEmbeddingRequest {
    /** Textos a procesar */
    texts: string[];
    /** Modelo a usar */
    model?: EmbeddingModel;
}

export interface BatchEmbeddingResult {
    /** Embeddings generados (mismo orden que texts) */
    embeddings: number[][];
    /** Modelo usado */
    model: EmbeddingModel;
    /** Total tokens */
    totalTokens: number;
}

// ============================================
// MODELOS SOPORTADOS
// ============================================

export type EmbeddingModel =
    | 'text-embedding-004'      // Vertex AI - 768 dims
    | 'text-multilingual-embedding-002'; // Multi-idioma

export const EMBEDDING_CONFIG = {
    'text-embedding-004': {
        dimensions: 768,
        maxTokens: 3072,
        provider: 'vertex-ai' as const,
    },
    'text-multilingual-embedding-002': {
        dimensions: 768,
        maxTokens: 2048,
        provider: 'vertex-ai' as const,
    },
} as const;

export const DEFAULT_EMBEDDING_MODEL: EmbeddingModel = 'text-embedding-004';

// ============================================
// INDEXACIÓN DE DOCUMENTOS
// ============================================

export interface IndexableDocument {
    /** ID del documento */
    id: string;
    /** Colección donde está almacenado */
    collectionName: string;
    /** Contenido a indexar */
    content: string;
    /** Título (alto peso en búsqueda) */
    title?: string;
    /** Metadata adicional */
    metadata?: Record<string, unknown>;
}

export interface IndexedDocument extends IndexableDocument {
    /** Embedding generado */
    embedding: number[];
    /** Modelo usado */
    embeddingModel: EmbeddingModel;
    /** Fecha de indexación */
    indexedAt: string;
    /** Hash del contenido (para detectar cambios) */
    contentHash: string;
}

export interface IndexingResult {
    /** Documento indexado */
    documentId: string;
    /** Si fue exitoso */
    success: boolean;
    /** Error si falló */
    error?: string;
    /** Tiempo de procesamiento (ms) */
    processingTime: number;
}

export interface BatchIndexingResult {
    /** Documentos procesados exitosamente */
    successful: number;
    /** Documentos que fallaron */
    failed: number;
    /** Detalles por documento */
    results: IndexingResult[];
    /** Tiempo total */
    totalTime: number;
}

// ============================================
// ESTADO DE ÍNDICES
// ============================================

export interface CollectionIndexStatus {
    /** Nombre de la colección */
    collectionName: string;
    /** Total documentos */
    totalDocuments: number;
    /** Documentos con embedding */
    indexedDocuments: number;
    /** Porcentaje completado */
    completionPercentage: number;
    /** Última actualización */
    lastUpdatedAt: string;
    /** Modelo usado */
    embeddingModel: EmbeddingModel;
}
